(() => {
  'use strict';
  const mobile = matchMedia('(max-width: 767px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const room = document.querySelector('[data-mobile-story]');
  const stage = room?.querySelector('.mobile-story-stage');
  const canvas = room?.querySelector('canvas');
  const hero = room?.closest('.hero');
  if (!room || !stage || !canvas || !hero) return;

  let dispose = () => {};
  let session = 0;
  let winkPlayed = false;

  async function configure() {
    const token = ++session;
    dispose();
    hero.classList.remove('scroll-ready');
    if (!mobile.matches || reduced.matches || document.querySelector('.signup-result, .signup-error')) return;

    const controller = new AbortController();
    const cache = new Map();
    const pending = new Set();
    const failures = new Set();
    let raf = 0;
    let observer;
    let resizeObserver;
    let flow;
    let travel;
    let content;
    const origins = [];
    let active = true;
    let enabled = false;
    let manifest;
    let wanted = 0;
    let shown = -1;
    let lastDirection = 1;
    let previousProgress = 0;
    let winkStarted = null;
    let winkRunning = false;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    function stop() {
      active = false;
      controller.abort();
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      resizeObserver?.disconnect();
      for (const image of cache.values()) image.close();
      cache.clear();
      for (const [node, anchor] of origins) anchor.replaceWith(node);
      flow?.remove();
      stage.style.height = '';
      canvas.style.transform = '';
      hero.classList.remove('scroll-ready');
    }
    dispose = stop;

    function arrangeScene() {
      flow = document.createElement('div');
      flow.className = 'mobile-story-flow';
      content = document.createElement('div');
      content.className = 'mobile-story-content';
      travel = document.createElement('div');
      travel.className = 'mobile-story-travel';
      travel.setAttribute('aria-hidden', 'true');
      content.append(travel);
      hero.querySelector('.hero-inner').append(flow);
      const nodes = [room, hero.querySelector('.early-access'), hero.querySelector('.hero-actions'), hero.querySelector('.calendar-feedback'), document.querySelector('.site-footer')];
      for (const node of nodes.filter(Boolean)) {
        const anchor = document.createTextNode('');
        node.before(anchor);
        origins.push([node, anchor]);
        (node === room ? flow : content).append(node);
      }
      flow.append(content);
    }

    function schedule() {
      if (active && !raf) raf = requestAnimationFrame(update);
    }

    async function load(index) {
      if (!active || cache.has(index) || pending.has(index) || failures.has(index)) return;
      pending.add(index);
      try {
        const url = new URL(manifest.frames[index], room.dataset.mobileStory);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Frame unavailable');
        const bitmap = await createImageBitmap(await response.blob());
        if (!active) { bitmap.close(); return; }
        cache.set(index, bitmap);
        while (cache.size > (previousProgress >= .7 ? 36 : 16)) {
          const victim = [...cache.keys()].filter(i => i !== shown && i !== wanted && !(previousProgress >= .7 && i >= manifest.winkStart && i <= manifest.winkEnd))
            .sort((a, b) => Math.abs(b - wanted) - Math.abs(a - wanted))[0];
          if (victim === undefined) break;
          cache.get(victim).close();
          cache.delete(victim);
        }
        if (!enabled && index === 0) {
          canvas.width = manifest.width;
          canvas.height = manifest.height;
          ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
          shown = 0;
          room.dataset.renderedFrame = '0';
          enabled = true;
          arrangeScene();
          hero.classList.add('scroll-ready');
        }
        schedule();
      } catch (error) {
        if (error.name !== 'AbortError') failures.add(index);
        if (index === 0 && active) stop();
      } finally {
        pending.delete(index);
      }
    }

    function prefetch() {
      if (!active || !enabled) return;
      const candidates = [wanted];
      for (let d = 1; d <= 7; d++) candidates.push(wanted + d * lastDirection, wanted - d * lastDirection);
      if (previousProgress >= .7 && !winkPlayed) {
        const winkFrames = Array.from({ length: manifest.winkEnd - manifest.winkStart + 1 }, (_, i) => manifest.winkStart + i);
        candidates.splice(1, 0, ...winkFrames, manifest.frameCount - 1);
      }
      if (winkRunning) candidates.unshift(wanted, Math.min(wanted + 1, manifest.frameCount - 1));
      for (const index of candidates) {
        if (pending.size >= 4) break;
        if (index >= 0 && index < manifest.frameCount) load(index);
      }
    }

    function update(now) {
      raf = 0;
      if (!active || !enabled) return;
      const rect = flow.getBoundingClientRect();
      const viewportHeight = parseFloat(getComputedStyle(stage).maxHeight);
      const pinnedTravel = Math.max(1, Math.min(travel.offsetHeight, flow.offsetHeight - viewportHeight));
      const entryLead = Math.max(0, Math.min(viewportHeight * .6, rect.top + window.scrollY));
      const length = pinnedTravel + entryLead;
      room.dataset.scrollLength = String(length);
      room.dataset.entryLead = String(entryLead);
      const progress = Math.max(0, Math.min(1, (entryLead - rect.top) / length));
      if (progress !== previousProgress) lastDirection = progress > previousProgress ? 1 : -1;
      previousProgress = progress;
      const nearEnd = progress >= .92;
      const winkReady = Array.from({ length: manifest.winkEnd - manifest.winkStart + 1 }, (_, i) => manifest.winkStart + i).every(i => cache.has(i));
      if (nearEnd && !winkPlayed && winkReady && rect.bottom > 0) {
        winkPlayed = true;
        winkRunning = true;
        winkStarted = now;
      }
      if (winkRunning && !nearEnd) winkRunning = false;
      if (winkRunning) {
        const amount = Math.min(1, (now - winkStarted) / 650);
        wanted = Math.round(manifest.winkStart + amount * (manifest.winkEnd - manifest.winkStart));
        if (amount === 1) { winkRunning = false; wanted = manifest.frameCount - 1; }
      } else {
        wanted = nearEnd && winkPlayed ? manifest.frameCount - 1 : Math.round(Math.min(1, progress / .92) * manifest.motionEnd);
      }
      room.dataset.scrollProgress = progress.toFixed(3);
      room.dataset.frame = String(wanted);
      room.dataset.wink = winkRunning ? 'playing' : winkPlayed ? 'complete' : 'waiting';
      const closest = cache.has(wanted) ? wanted : [...cache.keys()]
        .filter(i => i >= Math.min(shown, wanted) && i <= Math.max(shown, wanted))
        .sort((a, b) => Math.abs(a - wanted) - Math.abs(b - wanted))[0];
      if (closest !== undefined && closest !== shown) {
        ctx.drawImage(cache.get(closest), 0, 0, canvas.width, canvas.height);
        shown = closest;
        room.dataset.renderedFrame = String(shown);
      }
      const reveal = entryLead > 0 ? Math.max(0, Math.min(1, (entryLead - rect.top) / entryLead)) : 1;
      const settle = Math.max(0, Math.min(1, (progress - .7) / .3));
      const crop = .19 * (1 - reveal) + .19 * settle;
      const formTop = content.querySelector('.early-access').getBoundingClientRect().top;
      const faceSpace = formTop - rect.top - travel.offsetHeight;
      const sceneHeight = canvas.offsetHeight * (1 - crop);
      // During entry, the sticky stage still starts below the viewport top.
      const stageTop = Math.max(0, rect.top);
      const formOffset = formTop - stageTop;
      stage.style.height = `${Math.min(sceneHeight, Math.max(faceSpace, formOffset))}px`;
      canvas.style.transform = `translateY(${-crop * 100}%)`;
      prefetch();
      if (winkRunning) schedule();
    }

    try {
      const response = await fetch(room.dataset.mobileStory, { signal: controller.signal });
      if (!response.ok) throw new Error('Sequence unavailable');
      manifest = await response.json();
      if (!active || token !== session) return;
      if (!Array.isArray(manifest.frames) || !manifest.frames.length) throw new Error('Empty sequence');
      room.dataset.mobileStory = new URL(room.dataset.mobileStory, location.href).href;
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(stage);
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { load(0); observer.disconnect(); }
      }, { rootMargin: '100% 0px' });
      observer.observe(room);
    } catch {
      if (active) stop();
    }
  }
  mobile.addEventListener('change', configure);
  reduced.addEventListener('change', configure);
  configure();
})();
